import { Link } from 'react-router-dom';
import { platformLabel } from '../../lib/platforms';
import type { PlannedPostRead } from '../../types/api';
import { describeAutomation } from '../calendar/automation';
import { monthOf, parseIsoDate } from '../calendar/calendarDates';
import { PanelLink } from './PanelLink';

interface ThisWeekProps {
  /** Plans already narrowed to the week, see `plansThisWeek`. */
  plans: readonly PlannedPostRead[];
  today: string;
  isLoading: boolean;
  error: string | null;
}

/** "Mon 6" — the weekday and the day of the month, in the viewer's locale. */
function dayLabel(iso: string): string {
  const date = parseIsoDate(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.toLocaleDateString(undefined, { weekday: 'short' })} ${date.getDate()}`;
}

function dayPath(iso: string): string {
  return `/calendar?month=${monthOf(iso)}&view=list&day=${iso}`;
}

/**
 * The next seven days of the calendar, days with nothing on them left out.
 * An automated plan carries the calendar's own state glyph, the words in its
 * accessible label. Reading it spends nothing.
 */
export function ThisWeek({ plans, today, isLoading, error }: ThisWeekProps) {
  const days = new Map<string, PlannedPostRead[]>();
  for (const plan of plans) {
    const list = days.get(plan.scheduled_on) ?? [];
    list.push(plan);
    days.set(plan.scheduled_on, list);
  }

  return (
    <section className="home-panel" aria-labelledby="this-week-title">
      <div className="home-panel__head">
        <h2 className="home-panel__title" id="this-week-title">
          This week
        </h2>
        <PanelLink to={`/calendar?month=${monthOf(today)}`}>Open calendar</PanelLink>
      </div>

      {isLoading ? (
        <p className="kb-section__status" role="status">
          Reading the calendar
        </p>
      ) : error ? (
        <p className="home-panel__error" role="alert">
          {error}
        </p>
      ) : days.size === 0 ? (
        <p className="home-panel__empty">
          Nothing planned this week. <Link to="/calendar">Plan a post</Link>
        </p>
      ) : (
        <ul className="week-list">
          {[...days.entries()].map(([day, dayPlans]) => (
            <li className="week-list__day" key={day}>
              <span className="week-list__date">{day === today ? 'Today' : dayLabel(day)}</span>
              <ul className="week-list__plans">
                {dayPlans.map((plan) => {
                  const automation = describeAutomation(plan);
                  return (
                    <li key={plan.id}>
                      <Link className="week-list__plan" to={dayPath(day)}>
                        {automation ? (
                          <span
                            className={`entry-chip__glyph entry-chip__glyph--${plan.automation_status}`}
                            role="img"
                            aria-label={automation}
                            title={automation}
                          />
                        ) : null}
                        <span className="week-list__title">{plan.title}</span>
                        {plan.platform ? (
                          <span className="week-list__platform">
                            {platformLabel(plan.platform)}
                          </span>
                        ) : null}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
